import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom'; 
import socketService from '../services/socketService'; 
import Layout from '../components/Layout'; 
import moment from 'moment-timezone';

// --- Seen Status Update Modal ---
const SeenUpdateModal = ({ log, onClose, fetchLogs }) => {
    const [operatorName, setOperatorName] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!operatorName.trim()) return alert("অপারেটরের নাম লিখুন!");
        
        setLoading(true);
        socketService.emit('update_sms_seen_by_status', { 
            id: log._id, 
            value: operatorName 
        });
    };

    useEffect(() => {
        const handleResponse = (updatedLog) => {
            if (updatedLog && updatedLog._id) {
                setLoading(false);
                fetchLogs(); 
                onClose();   
            }
        };

        socketService.on('seen_by_updated_success', handleResponse);
        return () => socketService.off('seen_by_updated_success', handleResponse);
    }, [fetchLogs, onClose]);

    if (!log) return null;

    const modalJSX = (
        <div 
            style={{ 
                position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            onClick={onClose}
        >
            <div 
                className="modal-dialog" 
                style={{ width: '100%', maxWidth: '400px', pointerEvents: 'auto' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="card shadow-lg border-0" style={{ borderRadius: '15px', background: '#fff' }}>
                    <div className="card-header bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <h6 style={{ fontWeight: '800', color: '#0d6efd', margin: 0 }}>📩 Update Status</h6>
                        <button type="button" className="btn-close shadow-none" onClick={onClose}></button>
                    </div>
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '8px' }}>
                                    NEW STATUS / OPERATOR NAME
                                </label>
                                <input 
                                    type="text" 
                                    className="form-control shadow-none" 
                                    placeholder="Enter new status..." 
                                    value={operatorName} 
                                    onChange={(e) => setOperatorName(e.target.value)}
                                    required
                                    autoFocus
                                    style={{ padding: '12px', borderRadius: '10px', border: '2px solid #f1f5f9', background: '#f8fafc' }}
                                />
                            </div>
                            <div className="d-flex gap-2">
                                <button type="submit" className="btn btn-primary w-100 py-2" disabled={loading} style={{ borderRadius: '10px', fontWeight: '700' }}>
                                    {loading ? 'Updating...' : 'Update Status'}
                                </button>
                                <button type="button" className="btn btn-light border px-4" onClick={onClose} style={{ borderRadius: '10px' }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalJSX, document.body);
};

// --- Main SMSReceiveUser Component ---
const SMSReceiveUser = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const usernameFromUrl = queryParams.get('username') || '';

    // States
    const [users, setUsers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalLogs, setTotalLogs] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [logToSeen, setLogToSeen] = useState(null);

    // ম্যানুয়াল জব স্টেট
    const [userDevices, setUserDevices] = useState([]);
    const [manualJob, setManualJob] = useState({
        deviceName: '',
        trxID: '',
        amount: '',
        last3: ''
    });

    const [filters, setFilters] = useState({
        startDate: '', endDate: '', senderID: '', seenBy: '', 
        trxID: '', last3Digits: '', amount: '', username: usernameFromUrl
    });

    // ১. ডাটা লোডিং (ইউজার, ডিভাইস, জব রেসপন্স)
    useEffect(() => {
        if (!socketService.connected) socketService.connect();
        socketService.emit('get_all_users'); 
        
        const handleUsers = (data) => setUsers(data || []);
        const handleDevices = (data) => setUserDevices(data || []);
        const handleJobResponse = (data) => {
            if(data.success) {
                alert(data.message);
                setManualJob({ deviceName: '', trxID: '', amount: '', last3: '' });
                // সফল হলে অটোমেটিক লগ রিফ্রেশ করার চেষ্টা
                // fetchLogs(); // এটি কল করতে হলে ডিপেন্ডেন্সি ঠিক করতে হবে, আপাতত ম্যানুয়াল সার্চ থাকুক
            } else {
                alert(data.message);
            }
        };
        
        socketService.on('receive_all_users', handleUsers);
        socketService.on('receive_active_devices', handleDevices);
        socketService.on('sms_job_response', handleJobResponse);
        
        return () => {
            socketService.off('receive_all_users', handleUsers);
            socketService.off('receive_active_devices', handleDevices);
            socketService.off('sms_job_response', handleJobResponse);
        };
    }, []);

    // ২. সিকিউরিটি এবং ডিভাইস লোড
    useEffect(() => {
        if (usernameFromUrl && users.length > 0) {
            const userExists = users.find(u => u.username === usernameFromUrl);
            
            if (userExists) {
                const isMainAdmin = localStorage.getItem('isMaster') === 'true';
                let allowedUsers = [];
                try {
                    allowedUsers = JSON.parse(localStorage.getItem('allowedUsers') || '[]');
                } catch (e) {
                    allowedUsers = [];
                }

                if (!isMainAdmin && allowedUsers.length > 0 && !allowedUsers.includes(userExists._id)) {
                    alert("⚠️ Access Denied! You do not have permission to view this user.");
                    window.location.href = '/dashboard';
                    return;
                }
                socketService.emit('get_active_devices', userExists._id);
            }
        }
    }, [users, usernameFromUrl]);

    // ৩. সার্চ ভ্যালিডেশন
    const isSearchValid = useMemo(() => {
        const { trxID, last3Digits, amount, startDate, endDate, senderID, seenBy } = filters;
        if (trxID && trxID.trim().length < 8) return false;
        if (last3Digits && !amount) return false;
        return !!(startDate || endDate || senderID || seenBy || trxID || last3Digits || amount);
    }, [filters]);

    const extractAmountNumber = (message) => {
        const match = message.match(/(?:Amount|Tk)[:\s]*([\d,.]+)/i);
        if (match) return parseFloat(match[1].replace(/,/g, '')) || 0;
        return 0;
    };

    const extractAmountText = (message) => {
        const amt = extractAmountNumber(message);
        return amt > 0 ? `${amt.toLocaleString()} Tk.` : 'N/A';
    };

    const totalAmountSum = useMemo(() => {
        if (!isSearchValid) return 0;
        // এখানে ফিল্টার লজিক থেকে seenBy বাদ দেওয়া হয়েছে যাতে সব ডাটার সামেশন আসে
        return logs.reduce((sum, log) => sum + extractAmountNumber(log.message), 0);
    }, [logs, isSearchValid]);

    const formatDate = (dateStr) => {
        return moment(dateStr).tz('Asia/Dhaka').format('DD MMM, YYYY');
    };

    const formatTime = (dateStr) => {
        return moment(dateStr).tz('Asia/Dhaka').format('hh:mm A');
    };

    // ৪. লগ ফেচিং
    const fetchLogs = useCallback(() => {
        if (filters.trxID && filters.trxID.trim().length < 8) return alert("TrxID অবশ্যই কমপক্ষে ৮ অক্ষরের হতে হবে!");
        if (filters.last3Digits && !filters.amount) return alert("লাস্ট ৩ ডিজিট দিলে অবশ্যই Amount দিতে হবে!");

        if (!isSearchValid) {
            setLogs([]); setTotalLogs(0); return;
        }

        setLoading(true);
        const searchParams = {
            username: filters.username, 
            trxID: filters.trxID,
            last3: filters.last3Digits,
            amount: filters.amount,
            startDate: filters.startDate,
            endDate: filters.endDate,
            seenFilter: filters.seenBy,
            page: currentPage
        };
        socketService.emit('get-initial-sms-user', searchParams); 
    }, [filters, currentPage, isSearchValid]);

    useEffect(() => {
        if (!socketService.connected) socketService.connect();
        const handleLogsList = (data) => {
            setLogs(data.logs || []);
            setTotalLogs(data.totalDocs || 0);
            setLoading(false);
        };
        socketService.on('initial_sms_logs_user', handleLogsList);
        
        if (isSearchValid) fetchLogs();
        else { setLogs([]); setTotalLogs(0); }

        return () => socketService.off('initial_sms_logs_user', handleLogsList);
    }, [fetchLogs, isSearchValid]);

    const totalPages = Math.ceil(totalLogs / itemsPerPage);

    const renderPageNumbers = () => {
        const pageNumbers = [];
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + 4);
        if (endPage - startPage < 4) startPage = Math.max(1, endPage - 4);
        for (let i = startPage; i <= endPage; i++) {
            if (i > 0) pageNumbers.push(
                <button key={i} className={`page-btn ${currentPage === i ? 'active' : ''}`} onClick={() => setCurrentPage(i)}>{i}</button>
            );
        }
        return pageNumbers;
    };

    const handleManualJobSubmit = (e) => {
        e.preventDefault();
        
        // 🔥 পরিবর্তন: এখন শুধু Device এবং TrxID চেক করা হচ্ছে। Amount ও Last 3 অপশনাল।
        if(!manualJob.deviceName || !manualJob.trxID) {
            return alert("Device এবং TrxID অবশ্যই দিতে হবে!");
        }

        if (window.confirm("আপনি কি নিশ্চিত যে এই TrxID চেক করে SMS আপলোড করবেন?")) {
            socketService.emit('check_and_upload_sms_job', {
                username: usernameFromUrl,
                deviceName: manualJob.deviceName,
                trxID: manualJob.trxID,
                amount: manualJob.amount,
                last3Digits: manualJob.last3
            });
        }
    };

    // 🔥 নতুন: স্ট্যাটাস ব্যাজ রেন্ডারার ফাংশন
    const renderStatusBadge = (log) => {
        // ১. যদি Unseen হয় (seenBy নেই)
        if (!log.seenBy) {
            return <span className="status-pending">Pending</span>; // ক্লিক করা যাবে না
        }
        
        // ২. যদি Verified হয় -> আপডেট করা যাবে
        if (log.seenBy === 'Verified') {
            return (
                <span className="status-verified" onClick={() => setLogToSeen(log)}>
                    Verified <small>✏️</small>
                </span>
            );
        }

        // ৩. যদি Checked-Failed হয় -> শুধু দেখাবে
        if (log.seenBy === 'Checked-Failed') {
            return <span className="status-failed">Checked-Failed</span>;
        }

        // ৪. অন্যান্য স্ট্যাটাস -> শুধু দেখাবে
        return <span className="status-seen">{log.seenBy}</span>;
    };

    return (
        <Layout>
            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; padding: 20px; width: 100%; }
                .section-title { color: #0d6efd; font-weight: 700; }
                .badge-custom { background: #fff; padding: 7px 18px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .badge-custom span { color: #0d6efd; font-weight: 800; }
                .badge-amount { background: #0d6efd; color: #fff !important; }
                .badge-amount span { color: #fff !important; }
                .filter-card { background: #ffffff; padding: 20px; border-radius: 12px; box-shadow: 0 1px 10px rgba(0,0,0,0.05); margin-bottom: 20px; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 10px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; transition: 0.2s; }
                .modern-input:focus { border-color: #0d6efd; box-shadow: 0 0 0 2px rgba(13,110,253,0.1); }
                .btn-apply { background: #0d6efd; color: white; border-radius: 8px; font-size: 13px; border: none; font-weight: 600; cursor: pointer; padding: 10px 20px; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.05); min-height: 200px; display: flex; flex-direction: column; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table th { background: #f1f5f9; padding: 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; color: #475569; border-bottom: 2px solid #e2e8f0; }
                .custom-zebra-table td { padding: 15px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
                
                /* 🔥 আপডেটেড ব্যাজ স্টাইল */
                .status-pending { background: #fff7ed; color: #ea580c; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid #ffedd5; cursor: default; }
                .status-verified { background: #f0fdf4; color: #16a34a; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid #bbf7d0; cursor: pointer; transition: 0.2s; }
                .status-verified:hover { background: #dcfce7; transform: scale(1.05); }
                .status-failed { background: #fee2e2; color: #dc2626; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid #fecaca; cursor: default; }
                .status-seen { background: #e2e8f0; color: #475569; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid #cbd5e1; cursor: default; }

                .pagination-footer { display: flex; justify-content: flex-end; padding: 20px; background: #fff; border-top: 1px solid #f1f5f9; gap: 5px; margin-top: auto; }
                .page-btn { min-width: 35px; height: 35px; border-radius: 6px; border: 1px solid #dee2e6; background: #fff; cursor: pointer; }
                .page-btn.active { background: #0d6efd; color: #fff; border-color: #0d6efd; }
                .empty-state { padding: 60px; text-align: center; color: #94a3b8; font-weight: 500; }
                .error-text { font-size: 11px; color: #dc2626; font-weight: 600; margin-top: 4px; display: block; }
            `}</style>

            <div className="main-content-area">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="section-title mb-0">📩 SMS Search User ({usernameFromUrl})</h4>
                    <div className="d-flex gap-2">
                        <div className="badge-custom badge-amount">
                            Sum: <span>{totalAmountSum.toLocaleString()} Tk.</span>
                        </div>
                        <div className="badge-custom">
                            Total Logs: <span>{totalLogs}</span>
                        </div>
                    </div>
                </div>

                <div className="filter-card" style={{borderLeft: '5px solid #e11d48'}}>
                    <h6 style={{color: '#e11d48', fontWeight: 'bold', fontSize: '14px', marginBottom: '15px'}}>🚀 Check & Upload SMS Job (bKash)</h6>
                    <form onSubmit={handleManualJobSubmit} className="row g-2 align-items-end">
                        <div className="col-md-3">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>SELECT DEVICE</label>
                            <select className="modern-input" value={manualJob.deviceName} onChange={(e) => setManualJob({...manualJob, deviceName: e.target.value})} required>
                                <option value="">-- Choose Device --</option>
                                {userDevices.map(d => (<option key={d._id} value={d.deviceName}>{d.deviceName} ({d.mobileOnline ? '🟢 Online' : '⚪ Offline'})</option>))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>TRX ID</label>
                            <input type="text" className="modern-input" placeholder="TRX ID..." value={manualJob.trxID} onChange={(e) => setManualJob({...manualJob, trxID: e.target.value})} required />
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>LAST 3 DIGITS (Optional)</label>
                            <input type="text" className="modern-input" placeholder="123" value={manualJob.last3} onChange={(e) => setManualJob({...manualJob, last3: e.target.value})} />
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>AMOUNT (Optional)</label>
                            <input type="number" className="modern-input" placeholder="500" value={manualJob.amount} onChange={(e) => setManualJob({...manualJob, amount: e.target.value})} />
                        </div>
                        <div className="col-md-2">
                            <button type="submit" className="btn-apply w-100" style={{background: '#e11d48'}}>Check & Upload</button>
                        </div>
                    </form>
                </div>

                <div className="filter-card">
                    <form onSubmit={(e) => { e.preventDefault(); setCurrentPage(1); fetchLogs(); }} className="row g-3 align-items-end">
                        <div className="col-md-2">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>START DATE</label>
                            <input type="date" className="modern-input" value={filters.startDate} onChange={(e)=>setFilters({...filters, startDate: e.target.value})} />
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>END DATE</label>
                            <input type="date" className="modern-input" value={filters.endDate} onChange={(e)=>setFilters({...filters, endDate: e.target.value})} />
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>TRX ID</label>
                            <input type="text" className="modern-input" placeholder="Min 8 chars..." value={filters.trxID} onChange={(e)=>setFilters({...filters, trxID: e.target.value})} />
                            {filters.trxID && filters.trxID.length < 8 && <span className="error-text">Must be 8+ chars</span>}
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>LAST 3 DIGITS</label>
                            <input type="text" className="modern-input" placeholder="Need Amount..." value={filters.last3Digits} onChange={(e)=>setFilters({...filters, last3Digits: e.target.value})} />
                            {filters.last3Digits && !filters.amount && <span className="error-text">Amount is required</span>}
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>AMOUNT</label>
                            <input type="text" className="modern-input" placeholder="Amount..." value={filters.amount} onChange={(e)=>setFilters({...filters, amount: e.target.value})} />
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>SEEN BY</label>
                            <input type="text" className="modern-input" placeholder="Operator..." value={filters.seenBy} onChange={(e)=>setFilters({...filters, seenBy: e.target.value})} />
                        </div>
                        <div className="col-md-2 d-flex gap-2">
                            <button type="submit" className="btn-apply w-100 py-2">Search</button>
                            <button type="button" onClick={() => { setFilters({startDate: '', endDate: '', senderID: '', seenBy: '', trxID: '', last3Digits: '', amount: '', username: usernameFromUrl}); setCurrentPage(1); }} className="btn btn-light border w-100 py-2">Reset</button>
                        </div>
                    </form>
                </div>

                <div className="table-container shadow-sm">
                    {isSearchValid ? (
                        <>
                            <div className="table-responsive">
                                <table className="custom-zebra-table">
                                    <thead>
                                        <tr>
                                            <th>SIM & SENDER</th>
                                            <th>AMOUNT & DEVICE</th>
                                            <th>MESSAGE CONTENT</th>
                                            <th>DATE & TIME</th>
                                            <th>STATUS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!loading && logs.map((log) => (
                                            <tr key={log._id}>
                                                <td>
                                                    <b style={{ fontSize: '13px', display: 'block' }}>{log.simno}</b>
                                                    <span style={{ color: '#0d6efd', fontWeight: '700', fontSize: '12px' }}>{log.senderID}</span>
                                                    {log.operator && <div style={{fontSize:'10px', color:'#64748b'}}>{log.operator}</div>}
                                                </td>
                                                <td>
                                                    <b style={{ color: '#dc2626' }}>{extractAmountText(log.message)}</b>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{log.deviceName || 'N/A'}</div>
                                                </td>
                                                <td style={{ fontSize: '13px', color: '#475569', maxWidth: '350px' }}>
                                                    {log.message}
                                                    {log.isValid === false && <div className="text-danger small fw-bold mt-1">❌ Invalid Transaction</div>}
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: '700', fontSize: '13px' }}>{formatDate(log.receivedAt)}</div>
                                                    <div style={{ fontSize: '11px', color: '#64748b' }}>{formatTime(log.receivedAt)}</div>
                                                </td>
                                                <td>
                                                    {/* 🔥 নতুন স্ট্যাটাস রেন্ডার লজিক */}
                                                    {renderStatusBadge(log)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {!loading && logs.length === 0 && <div className="empty-state">No matching logs found.</div>}
                                {loading && <div className="empty-state">Loading data...</div>}
                            </div>

                            <div className="pagination-footer">
                                <button className="page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Prev</button>
                                {renderPageNumbers()}
                                <button className="page-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                            </div>
                        </>
                    ) : (
                        <div className="empty-state">
                             Enter a valid filter (Min 8 chars for TRX, Amount for Digits, or Date) to view logs.
                        </div>
                    )}
                </div>
            </div>

            <SeenUpdateModal 
                log={logToSeen} 
                onClose={() => setLogToSeen(null)} 
                fetchLogs={fetchLogs} 
            />
        </Layout>
    );
};

export default SMSReceiveUser;